<template>
  <div class="v2-images-section">
    <!-- Main Image -->
    <div>
      <div class="v2-flex v2-justify-between v2-items-center v2-mb-2">
        <p class="v2-images-section__title">
          Main Image
          <span class="v2-images-section__required">*</span>
        </p>
        <button
          class="v2-images-section__browse-btn"
          type="button"
          @click="handleBrowseImage('main')"
        >
          Browse Image
        </button>
      </div>
      <div class="v2-images-section__main-image-wrap">
        <img
          v-if="productImageUrls.length"
          class="v2-images-section__main-image"
          :src="productImageUrls[0]"
          alt="Product"
          @error="(event) => event.target.src = getDefaultProductImage(100, 100)"
        >
        <div
          v-else
          class="v2-images-section__placeholder"
        >
          No main image
        </div>
      </div>
      <div
        v-if="!productImageUrls.length"
        class="v2-images-section__error"
      >
        Main image is required
      </div>
    </div>

    <!-- Other Images -->
    <div class="v2-mt-4">
      <p class="v2-images-section__title">
        Other Images
      </p>
      <div
        v-if="productImageUrls.length > 1"
        class="v2-images-section__others"
      >
        <div
          v-for="(image, imageIndex) in productImageUrls.slice(1)"
          :key="imageIndex"
          class="v2-images-section__thumb"
        >
          <span class="v2-images-section__thumb-index">{{ imageIndex + 2 }}</span>
          <button
            class="v2-images-section__thumb-delete"
            type="button"
            @click="removeImage(image)"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
              />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
              />
            </svg>
          </button>
          <img
            class="v2-images-section__thumb-img"
            :src="image"
            alt="Product"
            @error="(event) => event.target.src = getDefaultProductImage(100, 100)"
          >
        </div>
      </div>

      <v2-drag-and-drop
        @drop="handleDrop"
        @browse-image="handleBrowseImage('other')"
      />
    </div>

    <input
      ref="fileInputMain"
      type="file"
      accept="image/*"
      hidden
      @change="handleFileChange"
    >
    <input
      ref="fileInputOther"
      type="file"
      accept="image/*"
      hidden
      multiple
      @change="handleFileChange"
    >
  </div>
</template>

<script>
import store from '@/store'
import { SET_PRODUCT_IMAGES } from '@/store/modules/productEdit.module'
import { getDefaultProductImage } from '@/@core/utils/utils'
import { apiToastError } from '@/@core/utils/toast'
import constants, { MAX_FILE_SIZE, PRODUCT_EDIT_VALID_IMAGE_EXTENSIONS } from '@/constants'
import analytics from '@/@core/utils/analytics'
import V2DragAndDrop from './V2DragAndDrop.vue'

export default {
  name: 'V2ImagesSection',
  components: {
    V2DragAndDrop,
  },
  data() {
    return {
      type: null,
      getDefaultProductImage,
    }
  },
  computed: {
    productImageUrls() {
      const images = store.state.productEdit.productImages || []
      return images.sort((a, b) => a.index - b.index).map(image => image.url)
    },
  },
  beforeDestroy() {
    this.cleanupLocalUrls()
  },
  methods: {
    handleBrowseImage(type) {
      if (this.productImageUrls.length === 10 && type === 'other') {
        apiToastError('You can upload maximum 10 images.')
        return
      }
      this.type = type
      this.$refs[`fileInput${type === 'main' ? 'Main' : 'Other'}`].click()
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_BROWSE_IMAGE_BUTTON, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        imageType: type,
      })
    },
    handleFiles(files) {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_SELECTS_IMAGE, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        imageType: this.type,
        imageNames: files.length ? Array.from(files).map(file => file.name) : [],
      })
      const localFiles = []
      const validFiles = Array.from(files).filter(file => PRODUCT_EDIT_VALID_IMAGE_EXTENSIONS.includes(file.type.split('/')[1]))

      if (validFiles.length !== files.length) {
        apiToastError('Invalid image format. Only PNG, JPG, JPEG, GIF, and WebP are allowed.')
      }
      if (validFiles.length === 0) return []

      validFiles.forEach((file, index) => {
        if (file.size > MAX_FILE_SIZE) {
          apiToastError(`"${file.name}" exceeds the 20MB size limit.`)
        } else {
          const localUrl = URL.createObjectURL(file)
          const localImage = {
            file,
            url: localUrl,
            index: this.type === 'main' ? 0 : (this.productImageUrls?.length + index + 1 || 0),
            isLocal: true,
          }
          localFiles.push(localImage)
        }
      })
      return localFiles
    },
    storeNewImages(localFiles) {
      let newImages = [...store.state.productEdit.productImages]
      if (this.type === 'main') {
        if (newImages.some(img => img.index === 0)) {
          newImages = newImages.map(img => (img.index === 0 ? localFiles[0] : img))
        } else {
          newImages.unshift(localFiles[0])
        }
      } else {
        newImages.push(...localFiles)
      }
      if (newImages.length > 10) {
        newImages = newImages.slice(0, 10)
        apiToastError('You can upload maximum 10 images.')
      }
      store.commit(SET_PRODUCT_IMAGES, newImages)
    },
    async handleFileChange(event) {
      const files = event.target.files
      if (!files || !files.length) return
      const localFiles = this.handleFiles(files)
      if (localFiles.length !== 0) {
        this.storeNewImages(localFiles)
      }
      event.target.value = null
    },
    handleDrop(files) {
      this.type = 'other'
      if (this.productImageUrls.length === 10) {
        apiToastError('You can upload maximum 10 images.')
        return
      }
      if (!files || !files.length) return
      const localFiles = this.handleFiles(files)
      this.storeNewImages(localFiles)
    },
    cleanupLocalUrls() {
      const localImages = store.state.productEdit.productImages
      const localImageUrls = localImages.filter(img => img.isLocal).map(img => img.url)
      localImageUrls.forEach(url => {
        URL.revokeObjectURL(url)
      })
      store.commit(SET_PRODUCT_IMAGES, localImages.filter(img => !img.isLocal))
    },
    removeImage(imageToDelete) {
      const image = store.state.productEdit.productImages.find(img => img.url === imageToDelete)
      if (!image) return
      const updatedImages = [...store.state.productEdit.productImages]
      if (image.isLocal) {
        URL.revokeObjectURL(image.url)
      }
      updatedImages.splice(updatedImages.indexOf(image), 1)
      const updatedImagesWithNewIndex = updatedImages.map((img, index) => ({
        ...img,
        index,
      }))
      store.commit(SET_PRODUCT_IMAGES, updatedImagesWithNewIndex)
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_REMOVE_IMAGE_ICON, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        imageType: 'other',
        imageUrl: imageToDelete,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-images-section {
  padding: var(--v2-space-4);
  border: 1px solid var(--v2-border);
  border-radius: var(--v2-radius-lg);
  min-width: 340px;
  max-width: 500px;

  &__title {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--v2-foreground);
    margin-bottom: 0;
  }

  &__required {
    color: var(--v2-destructive);
    font-weight: 400;
  }

  &__browse-btn {
    padding: var(--v2-space-1) var(--v2-space-2);
    background: var(--v2-muted-foreground);
    color: #fff;
    border: none;
    border-radius: var(--v2-radius-sm);
    font-size: 0.6875rem;
    font-weight: 500;
    font-family: var(--v2-font-sans);
    cursor: pointer;

    &:hover {
      background: var(--v2-foreground);
    }
  }

  &__main-image-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    background: var(--v2-muted);
    border-radius: var(--v2-radius-md);
    margin-top: var(--v2-space-2);
  }

  &__main-image {
    display: block;
    max-height: 165px;
    max-width: 100%;
    object-fit: contain;
  }

  &__placeholder {
    color: var(--v2-muted-foreground);
    font-size: 0.875rem;
  }

  &__error {
    color: var(--v2-destructive);
    font-size: 0.75rem;
    margin-top: var(--v2-space-1);
  }

  &__others {
    display: flex;
    flex-wrap: wrap;
    gap: var(--v2-space-3);
    margin-top: var(--v2-space-2);
    padding: 5px;
  }

  &__thumb {
    position: relative;
    width: 72px;
    height: 72px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-muted);
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: var(--v2-foreground);

      .v2-images-section__thumb-delete {
        display: flex;
      }
    }
  }

  &__thumb-index {
    position: absolute;
    top: 2px;
    left: 2px;
    font-size: 0.5625rem;
    background: var(--v2-muted-foreground);
    color: #fff;
    border-radius: 3px;
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }

  &__thumb-delete {
    position: absolute;
    top: -5px;
    right: -5px;
    cursor: pointer;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--v2-destructive);
    color: #fff;
    display: none;
    align-items: center;
    justify-content: center;
    z-index: 2;
    border: none;
    padding: 0;
  }

  &__thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--v2-radius-sm);
  }
}
</style>
